import {Polyline} from '@vis.gl/react-google-maps';
import {useTheme} from 'styled-components';

import {RoutePoint} from '../utils/getRouteBounds';
import {getRouteSegments} from '../utils/getRouteSegments';
import {interpolateColor} from '../utils/interpolateColor';

const ROUTE_STROKE_WEIGHT = 5;
// The white casing shows on each side of the route, so it is wider by twice the visible edge.
const CASING_STROKE_WEIGHT = ROUTE_STROKE_WEIGHT + 4;
// Enough color steps for a smooth gradient, few enough to keep the map light while it animates.
const MAX_GRADIENT_SEGMENTS = 24;
const CASING_Z_INDEX = 1;
const ROUTE_Z_INDEX = 2;
const ARROWS_Z_INDEX = 3;
// An open chevron pointing along the line, in symbol units.
const ARROW_SYMBOL_PATH = 'M -1.5,1.5 0,0 1.5,1.5';
const ARROW_SCALE = 3;
const ARROW_STROKE_WEIGHT = 2.5;
const ARROW_OFFSET = '40px';
const ARROW_REPEAT = '100px';

type RouteLineProps = {
  /** Number of points of the whole route, which fixes the gradient segments. */
  pointCount: number;
  /** The part of the route drawn so far, its last point may lie between two route points. */
  drawnPath: RoutePoint[];
};

/**
 * The route as a gradient from a light blue start to a dark blue finish on a white casing, with
 * chevrons that show the direction of the drive.
 */
export function RouteLine(props: RouteLineProps) {
  const theme = useTheme();
  const startColor = theme.colors.palettes.blue[40][100];
  const finishColor = theme.colors.palettes.blue[80][100];

  return (
    <>
      <Polyline
        path={props.drawnPath}
        strokeColor={theme.colors.general.white}
        strokeWeight={CASING_STROKE_WEIGHT}
        strokeOpacity={1}
        zIndex={CASING_Z_INDEX}
        clickable={false}
      />
      {getRouteSegments(props.pointCount, MAX_GRADIENT_SEGMENTS).map((segment) => (
        <Polyline
          key={segment.ratio}
          // Segments past the drawn end stay empty, the one with the end is cut at it.
          path={props.drawnPath.slice(segment.start, segment.end + 1)}
          strokeColor={interpolateColor(startColor, finishColor, segment.ratio)}
          strokeWeight={ROUTE_STROKE_WEIGHT}
          strokeOpacity={1}
          zIndex={ROUTE_Z_INDEX}
          clickable={false}
        />
      ))}
      <Polyline
        path={props.drawnPath}
        strokeOpacity={0}
        zIndex={ARROWS_Z_INDEX}
        clickable={false}
        icons={[
          {
            icon: {
              path: ARROW_SYMBOL_PATH,
              scale: ARROW_SCALE,
              strokeColor: theme.colors.general.white,
              strokeOpacity: 1,
              strokeWeight: ARROW_STROKE_WEIGHT,
            },
            offset: ARROW_OFFSET,
            repeat: ARROW_REPEAT,
          },
        ]}
      />
    </>
  );
}
